import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { toast } from "sonner";
import { Heart, X, SlidersHorizontal, Lock, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { HourlyCard, ShiftBadge, SourceBadge, payText, SHIFT_LABELS, CATEGORY_LABELS } from "@/components/HourlyBits";
import { api, errMsg } from "@/lib/api";

const Pick = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${on ? "border-slate-900 bg-slate-900 text-white" : "bg-white text-slate-700 hover:border-slate-400"}`}>{children}</button>
);
const flip = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export function PrefsDialog({ prefs, onSaved }) {
  const [p, setP] = useState(prefs);
  const [open, setOpen] = useState(false);
  useEffect(() => { setP(prefs); }, [prefs]);
  const save = async () => {
    try { const { data } = await api.put("/hourly/prefs", { ...p, min_pay: Number(p.min_pay) || 17 }); onSaved(data); setOpen(false); toast.success("Shift preferences saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="shift-prefs-open"><SlidersHorizontal className="mr-2 h-4 w-4" />My shift preferences</Button></DialogTrigger>
      <DialogContent className="bg-white sm:max-w-lg">
        <DialogHeader><DialogTitle>What shifts do you want?</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Shifts</Label>
            <div className="mt-2 flex flex-wrap gap-2">{Object.entries(SHIFT_LABELS).map(([k, t]) => <Pick key={k} on={p.shifts.includes(k)} onClick={() => setP({ ...p, shifts: flip(p.shifts, k) })} testid={`pref-shift-${k}`}>{t}</Pick>)}</div></div>
          <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Job types</Label>
            <div className="mt-2 flex flex-wrap gap-2">{Object.entries(CATEGORY_LABELS).map(([k, t]) => <Pick key={k} on={p.categories.includes(k)} onClick={() => setP({ ...p, categories: flip(p.categories, k) })} testid={`pref-cat-${k}`}>{t}</Pick>)}</div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Minimum pay ($/hr)</Label><Input type="number" min={17} max={100} value={p.min_pay} onChange={(e) => setP({ ...p, min_pay: e.target.value })} data-testid="pref-min-pay" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Country</Label>
              <div className="mt-1 flex gap-2">{[["", "Both"], ["US", "US"], ["CA", "Canada"]].map(([k, t]) => <Pick key={k || "all"} on={p.country === k} onClick={() => setP({ ...p, country: k })} testid={`pref-country-${k || "all"}`}>{t}</Pick>)}</div></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">State / province code</Label><Input maxLength={4} placeholder="e.g. TX, ON" value={p.state} onChange={(e) => setP({ ...p, state: e.target.value })} data-testid="pref-state" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">City</Label><Input placeholder="e.g. Dallas" value={p.city} onChange={(e) => setP({ ...p, city: e.target.value })} data-testid="pref-city" /></div>
          </div>
          <label className="flex items-center justify-between gap-3 rounded-xl border p-3 text-sm"><span><span className="font-medium">Email me new matching shift jobs</span><span className="block text-xs text-muted-foreground">At most once a day. Turn off anytime.</span></span>
            <Switch checked={p.alerts} onCheckedChange={(v) => setP({ ...p, alerts: v })} data-testid="pref-alerts-switch" /></label>
          <Button className="w-full" onClick={save} data-testid="shift-prefs-save">Save preferences</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SwipeCard({ j, onDone }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-12, 12]);
  const likeOpacity = useTransform(x, [20, 120], [0, 1]);
  const nopeOpacity = useTransform(x, [-120, -20], [1, 0]);
  const fling = (dir) => animate(x, dir * 600, { duration: 0.25, onComplete: () => onDone(dir > 0 ? "save" : "skip") });
  return (
    <div className="relative">
      <motion.div drag="x" style={{ x, rotate }} dragConstraints={{ left: 0, right: 0 }} dragElastic={0.9}
        onDragEnd={(_, info) => { if (Math.abs(info.offset.x) > 110) fling(Math.sign(info.offset.x)); }}
        className="relative cursor-grab touch-pan-y select-none rounded-3xl border bg-white p-6 shadow-lg active:cursor-grabbing" data-testid={`swipe-card-${j.job_id}`}>
        <motion.span style={{ opacity: likeOpacity }} className="absolute right-5 top-5 rounded-lg border-2 border-emerald-600 px-2 py-0.5 text-sm font-extrabold text-emerald-700">SAVE</motion.span>
        <motion.span style={{ opacity: nopeOpacity }} className="absolute left-5 top-5 rounded-lg border-2 border-rose-500 px-2 py-0.5 text-sm font-extrabold text-rose-600">SKIP</motion.span>
        <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{j.employer} · {CATEGORY_LABELS[j.hourly_category]}</p>
        <h3 className="mt-2 text-2xl font-bold leading-tight">{j.title}</h3>
        <p className="mt-2 text-sm text-slate-600">{j.location}</p>
        <p className="mt-5 font-mono text-3xl font-extrabold text-emerald-700" data-testid="swipe-pay">{payText(j)}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">{(j.shifts || []).map((s) => <ShiftBadge key={s} s={s} />)}<SourceBadge agency={j.is_agency} /></div>
        <p className="mt-5 text-xs text-muted-foreground">Shift fit {j.fit}/100 · based on your shifts, pay and skills</p>
      </motion.div>
      <div className="mt-5 flex items-center justify-center gap-6">
        <Button size="icon" variant="outline" className="h-14 w-14 rounded-full border-rose-200 text-rose-600" onClick={() => fling(-1)} aria-label="Skip" data-testid="swipe-skip"><X className="h-6 w-6" /></Button>
        <Button size="icon" className="h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => fling(1)} aria-label="Save" data-testid="swipe-save"><Heart className="h-6 w-6" /></Button>
      </div>
    </div>
  );
}

export function SwipeDeck({ prefsVersion }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [last, setLast] = useState(null);
  const load = () => api.get("/hourly/deck").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [prefsVersion]);
  if (!d) return <p className="text-sm text-muted-foreground">Loading your matches…</p>;
  if (d.locked) {
    return (
      <div data-testid="swipe-locked">
        <div className="mb-4 rounded-2xl bg-slate-950 p-5 text-white"><p className="font-semibold">{d.total.toLocaleString()} shift jobs match your preferences</p>
          <p className="mt-1 text-sm text-slate-300">Subscribe to swipe through them, save favourites and get daily alerts.</p>
          <Button className="mt-4 rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400" onClick={() => nav("/app/billing")} data-testid="swipe-upgrade"><Lock className="mr-2 h-4 w-4" />Unlock from $14.99/mo</Button></div>
        <div className="space-y-3">{d.items.map((j) => <HourlyCard key={j.teaser_id} j={j} onLocked={() => nav("/app/billing")} />)}</div>
      </div>
    );
  }
  const top = d.items[0];
  const done = async (action) => {
    const job = top;
    setD({ ...d, items: d.items.slice(1), total: Math.max(0, d.total - 1) });
    setLast(job);
    try { await api.post("/hourly/swipe", { job_id: job.job_id, action }); if (action === "save") toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); }
    if (d.items.length <= 2) load();
  };
  const undo = async () => { await api.delete(`/hourly/swipe/${last.job_id}`); setD({ ...d, items: [last, ...d.items], total: d.total + 1 }); setLast(null); };
  return (
    <div className="mx-auto max-w-md" data-testid="swipe-deck">
      <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground"><span data-testid="swipe-remaining">{d.total.toLocaleString()} matches left</span>
        {last && <button onClick={undo} className="inline-flex items-center gap-1 font-medium text-primary" data-testid="swipe-undo"><Undo2 className="h-4 w-4" />Undo</button>}</div>
      {top ? <SwipeCard key={top.job_id} j={top} onDone={done} />
        : <p className="rounded-2xl border bg-white p-8 text-center text-muted-foreground" data-testid="swipe-empty">You've seen every match. Widen your preferences or check back tomorrow.</p>}
      <p className="mt-4 text-center text-xs text-muted-foreground">Swipe right or tap the heart to save. Open saved jobs to see the full posting and apply.</p>
    </div>
  );
}

export function SavedList() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/hourly/saved").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!items.length) return <p className="rounded-2xl border bg-white p-8 text-center text-muted-foreground" data-testid="saved-empty">No saved shift jobs yet. Swipe right on jobs you like.</p>;
  return <div className="space-y-3" data-testid="saved-list">{items.map((j) => <HourlyCard key={j.job_id} j={j} />)}</div>;
}
